
// * ========================================================================
// * ages per day chart and table
// * ========================================================================

import CovidChart from './covid-chart.js';
import moment from 'moment';
import storage from './../storage';
import municipalityData from './municipality-data';
import dataHandler from './../data-handler';

const municipalitiesPerDay = (function(){
    let chartFirstDate = null;
    let chartData = null;
    let selectedMunicipality = [];

    const average = (arr) => arr.reduce((a, b) => a + b) / arr.length;

    function createChartData(data, options, selected) {
        const datasets = [];
        const multiselectOptions = [];

        const averageDataset = {
            label: '14 vrk ilmaantuvuus',
            name: '14 vrk ilmaantuvuus',
            id: 'always-visible',
            type: 'LineWithLine',
            fill: false,
            borderColor: '#000000',
            borderWidth: 2,
            data: [],
            pointRadius: 0,
            cubicInterpolationMode: 'default',
            lineTension: 0.6,
            yAxisID: 'line'
        };

        multiselectOptions.push(averageDataset);

        let idx = 0;
        for (const key of Object.keys(data.municipalities)) {
            const municipality = data.municipalities[key];
            if (municipality.count > 0) {
                const isSelected = selected.indexOf(municipality.id) !== -1;
                multiselectOptions.push({
                    type: 'bar',
                    label: key,
                    name: key,
                    id: municipality.id,
                    backgroundColor: options.largeColorPalette[idx],
                    count: municipality.count,
                    fill: true,
                    data: [],
                    //barPercentage: 1,
                    barThickness: 'flex',
                    offsetAfterAutoskip: true,
                    selected: isSelected
                });

                if (isSelected) { // Only one dataset
                    // 7 day average
                    datasets.push(averageDataset);
                    datasets.push({
                        type: 'bar',
                        label: key,
                        name: key,
                        id: municipality.id,
                        backgroundColor: options.largeColorPalette[idx],
                        count: municipality.count,
                        fill: true,
                        data: [],
                        //barPercentage: 1,
                        barThickness: 'flex',
                        offsetAfterAutoskip: true,
                        selected: true,
                    });

                    // Per date
                    let dayIdx = 0;
                    if (typeof data.municipality[key] !== 'undefined') {
                        for (const day of data.municipality[key].days) {
                            let firstDay = dayIdx - 7;
                            firstDay = firstDay < 0 ? 0 : firstDay;

                            const newDate = moment(day.date);
                            newDate.set({
                                hour: 0,
                                minute: 0,
                                second: 0
                            });

                            if (newDate >= chartFirstDate) {
                                const count = day.count - data.municipality[key].days[dayIdx-1].count; // Current day value only

                                datasets[1].data.push({ // Only one dataset
                                    x: newDate,
                                    y: count
                                });

                                if (day.rate_14) {
                                    datasets[0].data.push({ // Only one dataset
                                        x: newDate,
                                        y: day.rate_14
                                    });
                                }
                            }

                            dayIdx++;
                        }
                    }
                }
                idx++;
            }
        }

        return { datasets, multiselectOptions };
    }

    function initChart() {
        // Get data or use old datasets
        const municipalityName = selectedMunicipality.length && typeof chartData.municipalityIdMap[selectedMunicipality[0]] !== 'undefined' ? chartData.municipalityIdMap[selectedMunicipality[0]] : null;
        const date = municipalityName && typeof chartData.municipality[municipalityName] !== 'undefined' ? chartData.municipality[municipalityName].days[0].date : null;

        chartFirstDate = dataHandler.getChartFirstDate(date);
        let { datasets, multiselectOptions } = createChartData(chartData, window.config, selectedMunicipality);

        // Sort options alphabetically
        multiselectOptions.sort(function(a,b) {
            return a.name.localeCompare(b.name);
        });

        window.municipalitiesPerDayChart = new CovidChart('municipalities-per-day-chart', {
            datasets,
            chartOptions: {
                type: 'bar',
                options: {
                    plugins: {
                        tooltip: {
                            intersect: false,
                            position: 'nearest',
                            bodySpacing: 5,
                            callbacks: {
                                label: function(tooltipItem) {
                                    let label = tooltipItem.dataset.label + ': ' || '';
                                    label = label + tooltipItem.formattedValue;

                                    return label;
                                }
                            },
                        },
                    },
                    scales: {
                        x: {
                            type: 'timeseries',
                            stacked: false,
                            offset: true,
                            time: {
                                unit: 'day',
                                displayFormats: {
                                    day: 'D.M.YY',
                                }
                            },
                            ticks: {
                                autoSkip: true,
                                source: 'data'
                            }
                        },
                        y: {
                            min: 0,
                            ticks: {
                                precision: 0
                            }
                        },
                        'line': {
                            display: false,
                            stacked: false,
                            min: 0,
                            axis: 'y',
                            ticks: {
                                precision: 0
                            }
                        }
                    }
                }
            },
            config: window.config,
            tooltips: {
                elementId: 'municipalities-per-day-chart-tooltip'
            },
            multiselect: {
                requireCount: false,
                valueType: 'string',
                idField: 'id',
                singleSelect: true,
                closeOnChange: true,
                forceSelectedIds: ['average'],
                colorField: 'backgroundColor',
                inputName: 'municipalities-per-day-multiselect',
                selectElemId: 'municipalities-per-day-multiselect',
                colorElemId: 'municipalities-per-day-multiselect-colors',
                dropdownId: 'municipalities-per-day-multiselect-dropdown',
                multiselectOptions: multiselectOptions,
                data: chartData,
                reloadOnChange: reload
            },
            responsive: ['aspectRatio']
        });
    }

    /**
     * Initialize
     *
     * @param {Object} data - covid data
     */
    function init(data) {
        chartData = data;

        // Initially selected
        selectedMunicipality = storage.getData('municipalities-per-day-chart_multiselect');
        if (!selectedMunicipality || !Array.isArray(selectedMunicipality) || !selectedMunicipality.length) {
            selectedMunicipality = ['445171']; // Helsinki
        }

        municipalityData.load(chartData, selectedMunicipality, initChart);
    }

    function reload(data = null) {
        if (window.municipalitiesPerDayChart) {
            window.municipalitiesPerDayChart.chart.destroy();
            delete window.municipalitiesPerDayChart;

            // For some reason, chart wont go away sometimes

            // remove all content
            const chartContainer = document.querySelector('.municipalities-per-day-chart .chart-container');
            chartContainer.innerHTML = '';

            // Re create tooltip elem for same reason
            const tooltip = document.createElement('div');
            tooltip.classList.add('chart-tooltip');
            tooltip.id = 'municipalities-per-day-chart-tooltip';

            // Re-create canvas
            const canvasEl = document.createElement('canvas');
            canvasEl.id = 'municipalities-per-day-chart';
            canvasEl.width = 888;
            canvasEl.height= 440;

            chartContainer.appendChild(canvasEl);
            chartContainer.appendChild(tooltip);
        }

        init(data);
    }

    return {
        init,
        reload
    }

})();

export default municipalitiesPerDay;
